import React, { useContext, useMemo } from 'react';
import clsx from 'clsx';
import stylesShopping from './package-new-shopping.module.scss';
import stylesDefault from './package-new.module.scss';
import { usePackageTracking } from '../../hooks/analytics/usePackageTracking';
import { usePackage } from '../../hooks/usePackage';
import BuzzContext from '../../contexts/buzz';
import DestinationContext from '../../contexts/destination';
import { isInternalUrl } from '@buzzfeed/bf-utils/lib/internal-url';
import SectionTitle from '../SectionTitle';
import { CdnImage } from '@buzzfeed/react-components';
import { isNews } from '../../utils/isNews';
import { ShoppingBag } from '../Svgs/ShoppingBag';
import {
  DesktopTopLeft,
  DesktopBottomLeft,
  DesktopBottomRight,
  DesktopTopRight,
  MobileBottomLeft,
  MobileBottomRight,
  MobileTopRight,
} from './package-new-svgs';
import yourNewFavoriteThingSvg from '../../public/static/images/your_new_favorite_thing/your-new-favorite-thing.svg';
import shoppingCartSvg from '../../public/static/images/your_new_favorite_thing/shopping-cart.svg';
import frogLoveSvg from '../../public/static/images/your_new_favorite_thing/frog-love.svg';
import sunSvg from '../../public/static/images/your_new_favorite_thing/sun.svg';
import shoppingDividerSvg from '../../public/static/images/your_new_favorite_thing/shopping-divider.svg';
import dividerStyles from '../ShoppingDivider/shoppingDivider.module.scss';

function NewPackage({isShoppingDesign = false, variant = '', limitedItems}) {
  const { buzz = {} } = useContext(BuzzContext);
  const packageData = usePackage({ buzz });
  const packageCategory = packageData?.category || '';
  const packageTitle = 'Your New Favorite Thing';
  const styles = buzz.isShopping || isShoppingDesign ? stylesShopping : stylesDefault;
  // Use limitedItems prop if provided, otherwise default to 6
  const itemsToShow = limitedItems || 6;
  // Add special class for 3-item desktop layout in v2 experiment
  const isThreeItemDesktop = limitedItems === 3;

  if (!packageData) {
    return null;
  }

  const isShoppingPackage = buzz.isShopping || isShoppingDesign;
  const isWidePost = buzz.format?.page_width === 'wide';
  const isUS = buzz.classification?.edition === 'US' || !buzz.classification?.edition;

  return (
    <div className={clsx(
      styles.fullWidth,
      isShoppingDesign && styles.shoppingStyle,
      isThreeItemDesktop && styles.threeItemDesktop,
      variant && styles[variant],
      isUS && !isShoppingPackage && stylesShopping.decorated,
      isUS && !isShoppingPackage && isWidePost && stylesShopping.decoratedWide,
    )}>
      {isUS && <DecorativeSvgs styles={stylesShopping} />}
      {!isUS && isShoppingPackage && <LegacyDecorativeSvgs styles={styles} />}
      <section className={clsx(styles.shoppingWrapper, isNews(buzz) && styles.bottomShoppingList)}>
        <SectionTitle className={clsx(styles.sectionTitle, variant === 'refreshed' && styles.refreshedTitle)} id="new-shopping-package-title">
          {isUS ? (
            <>
              <img
                className={stylesShopping.titleImage}
                src={yourNewFavoriteThingSvg}
                alt=""
                role="presentation"
                aria-hidden="true"
                loading="lazy"
              />
              <span className={stylesShopping.visuallyHidden}>{packageTitle}</span>
            </>
          ) : (
            <>
              {variant === 'refreshed' && <ShoppingBag className={styles.icon} title={packageTitle} />}
              {packageTitle}
            </>
          )}
        </SectionTitle>
        <ul className={(variant === 'refreshed' || isNews(buzz)) ? styles.grid : styles.gridNarrow}>
          {packageData.results.slice(0, itemsToShow).map((item, index) => (
            <PackageListItem key={item.id} item={item} index={index + 1} category={packageCategory} styles={styles} />
          ))}
        </ul>
      </section>
      {isUS && !isShoppingPackage && (
        <div className={dividerStyles.commentDividerWrapper}>
          <img
            src={shoppingDividerSvg}
            alt=""
            aria-hidden="true"
            className={dividerStyles.divider}
          />
        </div>
      )}
    </div>
  );
}

const DECORATIVE_IMAGES = [
  { className: 'mobileBottomLeft', src: shoppingCartSvg },
  { className: 'mobileTopRight', src: frogLoveSvg },
  { className: 'mobileBottomRight', src: sunSvg },
  { className: 'desktopBottomLeft', src: shoppingCartSvg },
  { className: 'desktopTopRight', src: frogLoveSvg },
  { className: 'desktopBottomRight', src: sunSvg },
];

function DecorativeSvgs({ styles }) {
  return (
    <>
      {DECORATIVE_IMAGES.map(({ className, src }) => (
        <img
          key={className}
          className={styles[className]}
          src={src}
          alt=""
          role="presentation"
          aria-hidden="true"
          loading="lazy"
        />
      ))}
    </>
  );
}

function LegacyDecorativeSvgs({ styles }) {
  return (
    <>
      <MobileBottomLeft className={styles.mobileBottomLeft} title="Shopping Bag" />
      <MobileBottomRight className={styles.mobileBottomRight} title="Money Signs" />
      <MobileTopRight className={styles.mobileTopRight} title="Money Signs" />
      <DesktopTopLeft className={styles.desktopTopLeft} title="Top Left Decoration" />
      <DesktopBottomLeft className={styles.desktopBottomLeft} title="Bottom Left Decoration" />
      <DesktopTopRight className={styles.desktopTopRight} title="Top Right Decoration" />
      <DesktopBottomRight className={styles.desktopBottomRight} title="Bottom Right Decoration" />
    </>
  );
}

function PackageItem({ item, index, category, styles, imageLayout = 'stacked', imageSize = 'big' }) {
  const { destination } = useContext(DestinationContext);
  const targetTracking = isInternalUrl(item.url)
    ? { target_content_type: 'buzz', target_content_id: item.id }
    : { target_content_url: item.url, target_content_type: 'url', target_content_id: item.url };

  const unitTrackingData = useMemo(() => ({
    ...item,
    item_name: item.id,
    item_type: index === 0 ? 'splash' : 'card',
    position_in_subunit: index,
    index,
    category,
    ...targetTracking,
    data_source_name: 'site_component_api',
  }), [item, index, category, targetTracking]);

  const unitEl = usePackageTracking(unitTrackingData);

  return (
    <article>
      <a href={item.url} ref={unitEl} className={clsx(styles[imageLayout], styles.shoppingItemLink, styles[destination])}>
        <div className={clsx(styles.imageWrap, styles[imageSize])}>
          <CdnImage src={item.image} alt={item.image_alt_text} />
        </div>
        <span className={styles.text}>{item.name}</span>
      </a>
    </article>
  );
}

function PackageListItem({ item, index, category, styles }) {
  return (
    <li>
      <PackageItem item={item} index={index} category={category} styles={styles} />
    </li>
  );
}

export default NewPackage;
